import { useTranslation } from 'react-i18next';
import type { EnrolledMethods } from '@/modules/employees/domain/entities/Employee';
import type { ThemeTokens, BrandColors } from '@/shared/ui/theme/tokens';
import { FaceIcon, PinIcon, CardIcon } from './methodIcons';

interface Props {
  methods: EnrolledMethods;
  T: ThemeTokens;
  C: BrandColors;
  size?: 'sm' | 'md';
  showLabels?: boolean;
}

// Glyphs are imported from `./methodIcons` so MethodChips and MethodRow stay in sync.
const ITEMS = [
  { key: 'face' as const, label: 'Rostro',  Icon: FaceIcon },
  { key: 'pin'  as const, label: 'PIN',     Icon: PinIcon  },
  { key: 'card' as const, label: 'Tarjeta', Icon: CardIcon },
] as const;

const METHOD_COLOR = (key: 'face' | 'pin' | 'card', C: BrandColors): string => {
  if (key === 'face') return C.blue;
  if (key === 'pin')  return C.success;
  return C.teal;
};

export function MethodChips({ methods, T, C, size = 'sm', showLabels = false }: Props) {
  const { t } = useTranslation('common');
  const dim = size === 'md' ? 26 : 20;
  const ic  = size === 'md' ? 13 : 11;

  const METHOD_LABEL: Record<'face' | 'pin' | 'card', string> = {
    face: t('methods.face'),
    pin:  t('methods.pin'),
    card: t('methods.card'),
  };

  if (showLabels) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {ITEMS.map((m) => {
          const on = methods[m.key];
          const color = METHOD_COLOR(m.key, C);
          const Icon = m.Icon;
          return (
            <div
              key={m.key}
              title={on ? t(`methods.${m.key}Enrolled`) : t(`methods.${m.key}NotEnrolled`)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 999, background: on ? `${color}15` : T.rowBg, color: on ? color : T.textMuted, border: `1px solid ${on ? color + '38' : T.border}`, fontSize: 10, fontWeight: 700, opacity: on ? 1 : 0.55 }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Icon size={ic} />
              </span>
              <span>{METHOD_LABEL[m.key]}</span>
              {on && (
                <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {ITEMS.map((m) => {
        const on = methods[m.key];
        const color = METHOD_COLOR(m.key, C);
        const Icon = m.Icon;
        return (
          <div
            key={m.key}
            title={on ? t(`methods.${m.key}Enrolled`) : t(`methods.${m.key}NotEnrolled`)}
            style={{ width: dim, height: dim, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? `${color}18` : 'transparent', color: on ? color : T.border, border: on ? `1px solid ${color}40` : `1px dashed ${T.border}`, opacity: on ? 1 : 0.5 }}
          >
            <Icon size={ic} />
          </div>
        );
      })}
    </div>
  );
}
